/* global Rollbar */
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { sessionRoleType } from '../../redux/_constants';
import { PrefetchSession } from '../../apollo/queries/PrefetchSession.graphql';
import { PrefetchSessionById } from '../../apollo/queries/PrefetchSessionById.graphql';
import { PrefetchSessionPublic } from '../../apollo/queries/PrefetchSessionPublic.graphql';
import { PrefetchSessionPublicByKey } from '../../apollo/queries/PrefetchSessionPublicByKey.graphql';
import { useCallback, useEffect } from 'react';
import { useQueryParams } from '../../utilities/query';
import jwtDecode from 'jwt-decode';

/**
 * Hook to determine if the current user role has access to sensitive session data
 * like judge assignments based on their role.
 *
 * @returns {boolean} true if user should have access to sensitive data
 */
function useHasSensitiveAccess() {
  const sessionRole = useSelector((state) => state.session.role);
  const user = useSelector((state) => state.user.user);
  const queryParams = useQueryParams();
  const bypassAuth = queryParams.get('bypass_auth') === 'true';

  // Check Cognito groups for admin access (works even before session is loaded)
  const cognitoGroups =
    user?.signInUserSession?.accessToken?.payload?.['cognito:groups'] || [];
  const isCognitoAdmin =
    cognitoGroups.includes('admin') || cognitoGroups.includes('superadmin');

  // Validate PDF token for Lambda-generated requests
  const pdfToken = queryParams.get('pdfToken');
  let validPdfToken = false;
  if (pdfToken) {
    try {
      const decoded = jwtDecode(pdfToken);

      // Check token purpose
      if (decoded.purpose !== 'pdf-generation') {
        console.warn('[PDF Auth] Token has invalid purpose:', decoded.purpose);
      } else if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        // Check expiry (exp is in seconds, Date.now() is in milliseconds)
        console.warn('[PDF Auth] Token has expired');
      } else {
        validPdfToken = true;
      }
    } catch (err) {
      console.warn('[PDF Auth] Invalid PDF token:', err.message);
    }
  }

  const hasAccess =
    [sessionRoleType.ADMIN, sessionRoleType.JUDGE].includes(sessionRole) ||
    isCognitoAdmin ||
    bypassAuth ||
    validPdfToken;

  // Only ADMIN and JUDGE roles should have access to sensitive data like judgeAssignments
  // OR if user is in Cognito admin/superadmin group (works before session is loaded)
  // OR if bypass_auth is set to true (for testing/development)
  // OR if valid PDF token is present (for Lambda PDF generation)
  return hasAccess;
}

/**
 * Hook that automatically selects the appropriate session query based on user role.
 * Uses privileged queries for admin/judge users and public queries for others.
 *
 * @param {Object} options - Query options
 * @param {string} [options.sessionId] - The session ID to query (for getSession queries)
 * @param {string} [options.sessionKey] - The session key to query (for SessionByKey queries)
 * @param {Object} [options.queryOptions={}] - Additional Apollo query options
 * @returns {Object} Apollo query result
 */
export function useRoleBasedSessionQuery({
  sessionId,
  sessionKey,
  queryOptions = {},
}) {
  const hasSensitiveAccess = useHasSensitiveAccess();

  // Determine if we're using sessionKey or sessionId
  const useSessionKey = !!sessionKey;
  const identifier = useSessionKey ? sessionKey : sessionId;

  // Select appropriate query based on user role and identifier type
  let query;
  let variables;

  if (useSessionKey) {
    // Using sessionKey - need SessionByKey queries
    // - PrefetchSession: SessionByKey($sessionKey) with PrefetchSessionFragment (privileged)
    // - PrefetchSessionPublicByKey: SessionByKey($sessionKey) with PrefetchSessionFragmentPublic (public)
    query = hasSensitiveAccess ? PrefetchSession : PrefetchSessionPublicByKey;
    variables = { sessionKey: identifier };
  } else {
    // Using sessionId - use getSession queries
    // - PrefetchSessionById: getSession($id) with PrefetchSessionFragment (privileged)
    // - PrefetchSessionPublic: getSession($id) with PrefetchSessionFragmentPublic (public)
    query = hasSensitiveAccess ? PrefetchSessionById : PrefetchSessionPublic;
    variables = { id: identifier };
  }

  // Skip query if Lambda injected session data (for PDF generation)
  // The injected data already contains all session info including judges,
  // so making additional queries would fail due to lack of IAM auth in browser
  const hasInjectedData =
    typeof window !== 'undefined' && !!window.__INJECTED_SESSION_DATA__;

  const result = useQuery(query, {
    variables,
    skip: !identifier || hasInjectedData,
    errorPolicy: 'all', // Continue even if there are GraphQL errors
    ...queryOptions,
  });

  // Handle GraphQL errors related to unauthorized access to judgeAssignments or null athletes
  const handleGraphQLErrors = useCallback(
    (errors) => {
      if (!errors) return false;

      const hasJudgeAssignmentError = errors.some(
        (error) =>
          error.message &&
          (error.message.includes('judgeAssignments') ||
            error.message.includes('Unauthorized') ||
            error.message.includes('Access denied'))
      );

      const hasAthleteNullError = errors.some(
        (error) =>
          error.message &&
          error.message.includes(
            "Cannot return null for non-nullable type: 'Athlete'"
          )
      );

      if (hasJudgeAssignmentError && !hasSensitiveAccess) {
        console.warn(
          'Detected GraphQL error related to judgeAssignments for non-privileged user. ' +
            'This might indicate caching issues or incomplete role-based query implementation.'
        );
        return true;
      }

      if (hasAthleteNullError) {
        console.warn(
          'Detected GraphQL error related to null athlete in RosterLink. ' +
            'This indicates missing athlete data. Session will load with partial roster data.'
        );
        // Report to Rollbar for observability
        if (typeof Rollbar !== 'undefined') {
          Rollbar.error('GraphQL error: Null athlete in RosterLink', {
            errors: errors.filter((e) =>
              e.message.includes(
                "Cannot return null for non-nullable type: 'Athlete'"
              )
            ),
            context: 'useRoleBasedSessionQuery',
          });
        }
        return true; // Treat as handled, don't propagate as error
      }

      return false;
    },
    [hasSensitiveAccess]
  );

  // Check for GraphQL errors
  useEffect(() => {
    if (result.error && result.error.graphQLErrors) {
      handleGraphQLErrors(result.error.graphQLErrors);
    }
  }, [result.error, handleGraphQLErrors]);

  // Determine if we should filter out the error
  const shouldFilterError =
    result.error &&
    result.error.graphQLErrors &&
    handleGraphQLErrors(result.error.graphQLErrors);

  return {
    ...result,
    // Filter out handled errors
    error: shouldFilterError ? null : result.error,
  };
}

/**
 * Hook that provides the session data using role-based query selection.
 * This is a drop-in replacement for useSession that automatically handles
 * role-based access control.
 *
 * @param {string|Object} sessionIdOrOptions - The session ID or options object with sessionId/sessionKey
 * @param {Object} [queryOptions={}] - Additional Apollo query options
 * @returns {Object} Session data and loading state
 */
export function useRoleBasedSession(sessionIdOrOptions, queryOptions = {}) {
  // Handle both old signature useRoleBasedSession(sessionId) and new signature useRoleBasedSession({sessionId, sessionKey})
  const options =
    typeof sessionIdOrOptions === 'string'
      ? { sessionId: sessionIdOrOptions }
      : sessionIdOrOptions || {};

  const { sessionId, sessionKey } = options;

  const { data, loading, error } = useRoleBasedSessionQuery({
    sessionId,
    sessionKey,
    queryOptions,
  });

  // Extract session data from either getSession (sessionId) or SessionByKey (sessionKey)
  const session = data?.getSession || data?.SessionByKey?.items?.[0] || {};

  return {
    session,
    loading,
    error,
  };
}

/**
 * Helper hook to automatically determine session identifier (sessionId vs sessionKey)
 * based on available props and query parameters
 *
 * @param {string} [sessionId] - Explicit session ID
 * @returns {Object} Object with sessionId and/or sessionKey
 */
export function useSessionIdentifier(sessionId) {
  const queryParams = useQueryParams();
  const querySessionKey = queryParams.get('s');

  // If we have sessionId, prefer it
  if (sessionId) {
    return { sessionId };
  }

  // Otherwise, use sessionKey from query params
  if (querySessionKey) {
    return { sessionKey: querySessionKey };
  }

  // No identifier available
  return {};
}

export { useHasSensitiveAccess };
